import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useQuery, useQueryClient } from "react-query";
import { getUserPermissions } from "../../services/user/permissions";
import Session from "../../utils/session";
import { ClientPermissions, Permission } from "../../models/permissions";




interface UserContextInterface {
  userPermissions: ClientPermissions | null;
  getFeaturePermissions: ((feature: string) => Permission | null) | null;
  hasPermission: (feature: string, permission: string) => boolean;
  accessToken: string | null;
  setAccessToken: (feature: string, permission: string) => void;
  refreshToken: string | null;
  setRefreshToken: (feature: string, permission: string) => void;
  login: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  loading: boolean;
  setLoading: (val: boolean) => void;
  sessionReady: boolean;
  // Only meaningful when userPermissions?.availableOrgs has more than one
  // entry — switches which of this email's own org accounts is active.
  switchOrg: (orgId: number) => void;
}

const UserContext = createContext<UserContextInterface>({
  userPermissions: null,
  getFeaturePermissions: () => null,
  hasPermission: () => false,
  accessToken: null,
  setAccessToken: () => { },
  refreshToken: null,
  setRefreshToken: () => { },
  login: () => { },
  logout: () => { },
  loading: false,
  setLoading: () => null,
  sessionReady: false,
  switchOrg: () => { },
});

export function UserProvider({ children }: PropsWithChildren) {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    if (Session.isLoggedIn()) {
      setAccessToken(Session.getToken());
      setRefreshToken(Session.getRefreshToken());
    } else {
      setAccessToken(null);
      setRefreshToken(null);
    }
    setSessionReady(true);
  }, []);


  // Check if user is a merchant by looking at session storage
  const isMerchant = (): boolean => {
    const merchantData = sessionStorage.getItem('merchant_data');
    return merchantData !== null;
  };

  const queryClient = useQueryClient();

  const { data: clientPermission, isLoading } = useQuery<ClientPermissions>(
    "permissions-api",
    async (): Promise<ClientPermissions> => {
      let { data } = await getUserPermissions();
      return data.data || null;
    },
    {
      enabled: accessToken != null && !isMerchant(), // Skip API call for merchants
      // Permissions don't change mid-session; avoid refetching on every window focus
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        // Pin whichever org account the backend resolved (explicit choice,
        // or its own "most recent" default) so a page reload keeps using
        // the same account instead of re-resolving independently.
        if (data?.orgId != null) {
          Session.setActiveOrgId(data.orgId);
        }
      },
    }
  );

  // Switching identity/role (which org account this email is acting as)
  // touches everything the app has cached for the old org — reload rather
  // than trying to selectively invalidate every org-scoped query.
  function switchOrg(orgId: number) {
    Session.setActiveOrgId(orgId);
    queryClient.clear();
    window.location.reload();
  }


  function getFeaturePermissions(feature: string): Permission | null {
    // For merchants, get permissions from session storage
    if (isMerchant()) {
      console.log(sessionStorage.getItem('merchant_data'), "merchantData");
      const merchantData = JSON.parse(sessionStorage.getItem('merchant_data') || '{}');
      if (merchantData.permissions) {
        return merchantData.permissions.find(
          (_feature: any) => _feature.module === feature
        ) || null;
      }
      return null;
    }

    // For admin users, use API permissions
    if (clientPermission != null) {
      return clientPermission?.permissions.find(
        (_feature) => _feature.module === feature
      )!
    } else {
      return null;
    }
  }

  function hasPermission(feature: string, permission: string) {
    // Super admins have unconditional access everywhere else (see
    // PrivateRoute and the drawer's nav filtering), regardless of whether a
    // given module is registered in their permissions payload yet — match
    // that here instead of relying on every module being present.
    if (!isMerchant() && clientPermission?.superAdmin) {
      return true;
    }

    let _feature = getFeaturePermissions(feature);

    return _feature?.actions.find(
      (_permission) => _permission === permission
    )
      ? true
      : false;

  }

  function login(accessToken: string, refreshToken: string) {
    Session.login(accessToken, refreshToken);
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
  }

  function logout() {
    Session.logout();
    setAccessToken(null);
    setRefreshToken(null);
  }



  // Get user permissions - use merchant data if available, otherwise use API data
  const getUserPermissionsData = (): ClientPermissions | null => {
    // console.log(sessionStorage.getItem('merchant_data'), "merchantData");
    if (isMerchant()) {
      const merchantData = JSON.parse(sessionStorage.getItem('merchant_data') || '{}');
      return merchantData.permissions ? {
        id: merchantData.merchant_id,
        name: merchantData.name,
        emailId: merchantData.email,
        roleId: 999,
        role: "merchant",
        superAdmin: false,
        permissions: merchantData.permissions
      } : null;
    }
    // console.log(clientPermission, "clientPermission");
    return clientPermission || null;
  };

  return (
    <UserContext.Provider
      value={{
        userPermissions: getUserPermissionsData(),
        getFeaturePermissions: getFeaturePermissions,
        hasPermission: hasPermission,
        accessToken,
        setAccessToken,
        refreshToken,
        setRefreshToken,
        login,
        logout,
        loading: isMerchant() ? false : isLoading, // No loading for merchants
        setLoading: () => { }, // No-op function since we don't use local loading state
        sessionReady,
        switchOrg,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export default function useUser() {
  return useContext(UserContext);
}
